import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import dieLineSvg from "../../assets/T-9x4-400x150-overlay.svg";
import ApiService from "../../services/ApiService";
import HomeService from "../../services/HomeService";
import Logger from "../../services/Logger";
import { trackEvent } from "../../services/GoogleAnalytics";
import { usePagesTranslation } from "../locales";
import SlidingPlayMain from "../../admin/story/SlidingPlayMain";
import { pickSlidingBrowseBoardPreview, pickSlidingCardImageUrl } from "../browse-sliding/slidingBrowseUtils";
import { TagChipAvatar } from "../puzzles/common/components/TagChipAvatar";
import CrossGrid from "../puzzles/crossword/CrossGrid";
import GridGrid from "../puzzles/grid/GridGrid";
import { fetchHomeStoryWordPuzzleGridPreview } from "./storybookGridPreview";
import {
    getHomeStoryRowId,
    getHomeStorySlidingJigsawLinks,
    getHomeStoryWordPuzzleLinkMeta,
    resolveHomeStoryPlayPath,
    resolveHomeStoryBlob,
} from "./storyRoUtils";
import { TEXT } from "../../dark-theme/palette";

const playButtonSx = {
    backgroundColor: "#3BA666",
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": { backgroundColor: "#2e7d32" },
};

/** Same card width and 1:1 preview for crossword, grid, sliding, and jigsaw. */
const linkedPuzzleCardOuterSx = {
    flex: { xs: "1 1 100%", sm: "1 1 240px" },
    maxWidth: 360,
    display: "flex",
    flexDirection: "column",
    alignSelf: "stretch",
};

const linkedPuzzlePreviewBoxSx = {
    position: "relative",
    width: "100%",
    borderRadius: 1,
    overflow: "hidden",
    bgcolor: "action.hover",
    border: "1px solid",
    borderColor: "divider",
    mb: 1.5,
    aspectRatio: "1 / 1",
    flexShrink: 0,
};

const linkedPuzzlePreviewInnerSx = {
    width: "100%",
    height: "100%",
    bgcolor: "#fff",
    pointerEvents: "none",
    userSelect: "none",
};

/** Fit crossword/grid inside the 1:1 card without clipping outer borders. */
const wordPuzzleGridPreviewSx = {
    ...linkedPuzzlePreviewInnerSx,
    boxSizing: "border-box",
    p: 1.25,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    "& .MuiPaper-root": {
        width: "100%",
        maxWidth: "100%",
        height: "auto",
        maxHeight: "100%",
        aspectRatio: "1",
        margin: "0 !important",
        boxSizing: "border-box",
        flexShrink: 1,
        minWidth: 0,
        minHeight: 0,
    },
};

function formatTemplateLanguageLabel(language, t) {
    const s = String(language || "").toLowerCase();
    if (s === "french" || s === "fr") return t("wordOfDay.content-lang-french");
    if (s === "english" || s === "en") return t("wordOfDay.content-lang-english");
    if (!s) return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function puzzleTypeLabel(type, t) {
    const up = String(type || "").toUpperCase();
    if (up === "CROSSWORD") return t("stories.type-crossword");
    if (up === "WORD_SEARCH") return t("stories.type-word-search");
    if (up === "HIDDEN_MESSAGE") return t("stories.type-hidden-message");
    return type || "—";
}

function pickStoryImageUrl(story) {
    if (!story || typeof story !== "object") return "";
    return (
        pickSlidingCardImageUrl(story) ||
        (typeof story.imagePreviewUrl === "string" && story.imagePreviewUrl.trim()) ||
        (typeof story.imageUrl === "string" && story.imageUrl.trim()) ||
        ""
    );
}

function pickDefaultJigsawTemplate(templates) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    const n = (t) => Number(t?.tileSize);
    const bySeed = {};
    for (const t of templates) {
        const name = t.templateName || t.name || "";
        const parts = String(name).split("-");
        const seed = parts.length >= 2 && parts[1].startsWith("S") ? parts[1] : "other";
        if (!bySeed[seed]) bySeed[seed] = [];
        bySeed[seed].push(t);
    }
    const firstGroup = bySeed[Object.keys(bySeed)[0]] || templates;
    return (
        firstGroup.find((t) => n(t) === 9) ||
        firstGroup.find((t) => n(t) === 6) ||
        firstGroup.find((t) => n(t) === 12) ||
        firstGroup[0]
    );
}

function JigsawDieLinesOverlay({ template }) {
    if (template?.piecePaths?.pieces) {
        return (
            <Box
                component="svg"
                sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                    zIndex: 1,
                }}
                viewBox="0 0 1536 1536"
                preserveAspectRatio="none"
            >
                {template.piecePaths.pieces.map((piece, index) => (
                    <path
                        key={index}
                        d={piece.path}
                        fill="none"
                        stroke="rgba(255, 255, 255, 0.75)"
                        strokeWidth="3"
                    />
                ))}
            </Box>
        );
    }
    if (template?.svgPath) {
        return (
            <Box
                component="img"
                src={template.svgPath}
                alt=""
                sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "fill",
                    pointerEvents: "none",
                    zIndex: 1,
                }}
            />
        );
    }
    return (
        <Box
            component="img"
            src={dieLineSvg}
            alt=""
            sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "fill",
                pointerEvents: "none",
                zIndex: 1,
            }}
        />
    );
}

function LinkedPuzzlePreviewBox({ loading, children }) {
    return (
        <Box sx={linkedPuzzlePreviewBoxSx}>
            {loading ? (
                <Box sx={{ ...linkedPuzzlePreviewInnerSx, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                children
            )}
        </Box>
    );
}

function LinkedImagePuzzleCard({
    sectionTitle,
    hint,
    imageUrl,
    playTo,
    playLabel,
    overlay,
    loading,
    slidingPreview,
    onPlayClick,
    disabled,
}) {
    if (disabled || !playTo) return null;

    return (
        <Box sx={linkedPuzzleCardOuterSx}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: TEXT.primary }}>
                {sectionTitle}
            </Typography>
            <LinkedPuzzlePreviewBox loading={loading}>
                {slidingPreview ? (
                    <Box sx={linkedPuzzlePreviewInnerSx}>
                        <SlidingPlayMain
                            imageUrl={slidingPreview.imageUrl}
                            gridSize={slidingPreview.gridSize}
                            startingLayout={slidingPreview.startingLayout}
                            hideCompletedCaption
                        />
                    </Box>
                ) : imageUrl ? (
                    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        <Box
                            component="img"
                            src={imageUrl}
                            alt=""
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                            }}
                        />
                        {overlay}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            ...linkedPuzzlePreviewInnerSx,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                        }}
                    >
                        <Typography variant="body2">—</Typography>
                    </Box>
                )}
            </LinkedPuzzlePreviewBox>
            {hint ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
                    {hint}
                </Typography>
            ) : null}
            <Box
                sx={{
                    mt: "auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 1,
                }}
            >
                {playTo ? (
                    <Button
                        component={RouterLink}
                        to={playTo}
                        variant="contained"
                        size="small"
                        onClick={onPlayClick}
                        sx={playButtonSx}
                    >
                        {playLabel}
                    </Button>
                ) : null}
            </Box>
        </Box>
    );
}

function WordPuzzleGridPreview({ preview }) {
    if (!preview?.grid?.length) return null;
    const noop = () => {};
    return (
        <Box sx={wordPuzzleGridPreviewSx}>
            <Box
                sx={{
                    width: "100%",
                    maxHeight: "100%",
                    aspectRatio: "1",
                    minWidth: 0,
                    minHeight: 0,
                }}
            >
            {preview.type === "CROSSWORD" ? (
                <CrossGrid
                    grid={preview.grid}
                    words={[]}
                    wordsFound={[]}
                    onClearClickedWords={noop}
                    onClickedWords={noop}
                    gridSize={12}
                    isFullscreen={false}
                    isBoldFont={false}
                    filledCoordinates={preview.filledCoordinates}
                    isHintMode={false}
                    highlightCells={[]}
                    hideAllWords={true}
                    clueNumberFontPx={9}
                />
            ) : (
                <GridGrid
                    grid={preview.grid}
                    words={[]}
                    wordsFound={[]}
                    onWordFound={noop}
                    onClickedWords={noop}
                    gridSize={12}
                    isFullscreen={false}
                    isBoldFont={false}
                    filledCoordinates={preview.filledCoordinates}
                    isHintMode={false}
                    highlightCells={[]}
                    hiddenMessageCells={[]}
                />
            )}
            </Box>
        </Box>
    );
}

function LinkedTextPuzzleCard({
    meta,
    playTo,
    playLabel,
    playLoading,
    gridPreview,
    gridLoading,
    t,
    onPlayClick,
}) {
    if (!meta || meta.disabled) return null;

    const languageLabel = formatTemplateLanguageLabel(meta.language, t);
    const sectionTitle = puzzleTypeLabel(meta.type, t);

    return (
        <Box sx={linkedPuzzleCardOuterSx}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: TEXT.primary }}>
                {sectionTitle}
            </Typography>
            <LinkedPuzzlePreviewBox loading={gridLoading}>
                {gridPreview?.grid?.length ? (
                    <Box sx={{ height: "100%" }}>
                        <WordPuzzleGridPreview preview={gridPreview} />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            ...linkedPuzzlePreviewInnerSx,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                        }}
                    >
                        <Typography variant="body2">—</Typography>
                    </Box>
                )}
            </LinkedPuzzlePreviewBox>
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 700,
                    mb: 0.75,
                    color: TEXT.primary,
                    lineHeight: 1.35,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                }}
            >
                {meta.title}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5, alignItems: "center", minHeight: 24 }}>
                {languageLabel ? (
                    <Chip size="small" label={languageLabel} variant="outlined" sx={{ fontWeight: 600 }} />
                ) : null}
                {meta.tags.length ? (
                    meta.tags.map((tag) => {
                        const tagKey = `puzzle.tags.${tag.toLowerCase()}`;
                        const fallback = tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
                        const translated = t(tagKey);
                        const displayLabel = translated !== tagKey ? translated : fallback;
                        return (
                            <Chip
                                key={tag}
                                avatar={<TagChipAvatar tag={tag} size={18} />}
                                label={displayLabel}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                        );
                    })
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        {t("stories.no-tags")}
                    </Typography>
                )}
            </Box>
            <Box
                sx={{
                    mt: "auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 1,
                    width: "100%",
                }}
            >
                {playTo ? (
                    <Button
                        component={RouterLink}
                        to={playTo}
                        variant="contained"
                        size="small"
                        onClick={onPlayClick}
                        sx={{ ...playButtonSx, alignSelf: "flex-start" }}
                    >
                        {playLabel}
                    </Button>
                ) : playLoading ? (
                    <CircularProgress size={22} />
                ) : null}
            </Box>
        </Box>
    );
}

/**
 * Linked puzzles under storybook reader: word puzzle (title/tags/language) and sliding/jigsaw (image + play).
 */
export default function StorybookLinkedPuzzles({ row }) {
    const { t } = usePagesTranslation();
    const { slidingPlayId, jigsawPlayId, slidingDisabled, jigsawDisabled } = useMemo(
        () => getHomeStorySlidingJigsawLinks(row),
        [row]
    );
    const wordPuzzleMeta = useMemo(() => getHomeStoryWordPuzzleLinkMeta(row), [row]);
    const storyBlob = useMemo(() => resolveHomeStoryBlob(row), [row]);
    const storyImageUrl = useMemo(() => pickStoryImageUrl(storyBlob), [storyBlob]);
    const storyId = useMemo(() => getHomeStoryRowId(row), [row]);

    const trackStorybookPlay = (puzzleType, playPath) => {
        trackEvent("storybook_puzzle_play", {
            story_id: storyId ?? undefined,
            puzzle_type: puzzleType,
            play_path: playPath,
        });
    };

    const [slidingBoardPreview, setSlidingBoardPreview] = useState(null);
    const [slidingFallbackImageUrl, setSlidingFallbackImageUrl] = useState("");
    const [slidingLoading, setSlidingLoading] = useState(false);
    const [jigsawTemplate, setJigsawTemplate] = useState(null);
    const [jigsawLoading, setJigsawLoading] = useState(false);
    const [wordPlayPath, setWordPlayPath] = useState(undefined);
    const [wordGridPreview, setWordGridPreview] = useState(null);
    const [wordGridLoading, setWordGridLoading] = useState(false);

    useEffect(() => {
        if (!wordPuzzleMeta) {
            setWordPlayPath(undefined);
            return undefined;
        }
        let cancelled = false;
        setWordPlayPath(undefined);
        resolveHomeStoryPlayPath(row)
            .then((path) => {
                if (!cancelled) setWordPlayPath(path ?? null);
            })
            .catch(() => {
                if (!cancelled) setWordPlayPath(null);
            });
        return () => {
            cancelled = true;
        };
    }, [row, wordPuzzleMeta]);

    useEffect(() => {
        if (!wordPuzzleMeta) {
            setWordGridPreview(null);
            setWordGridLoading(false);
            return undefined;
        }
        let cancelled = false;
        setWordGridLoading(true);
        fetchHomeStoryWordPuzzleGridPreview(row)
            .then((preview) => {
                if (!cancelled) setWordGridPreview(preview);
            })
            .catch(() => {
                if (!cancelled) setWordGridPreview(null);
            })
            .finally(() => {
                if (!cancelled) setWordGridLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [row, wordPuzzleMeta]);

    useEffect(() => {
        if (!slidingPlayId) {
            setSlidingBoardPreview(null);
            setSlidingFallbackImageUrl("");
            setSlidingLoading(false);
            return undefined;
        }
        let cancelled = false;
        setSlidingLoading(true);
        (async () => {
            try {
                const group = await HomeService.getSlidingStoryGroup(slidingPlayId);
                if (cancelled) return;
                const preview = pickSlidingBrowseBoardPreview(group);
                setSlidingBoardPreview(preview);
                const fromGroup = pickStoryImageUrl(group?.story);
                setSlidingFallbackImageUrl(fromGroup || storyImageUrl);
            } catch (e) {
                Logger.error("StorybookLinkedPuzzles sliding:", e);
                if (!cancelled) {
                    setSlidingBoardPreview(null);
                    setSlidingFallbackImageUrl(storyImageUrl);
                }
            } finally {
                if (!cancelled) setSlidingLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [slidingPlayId, storyImageUrl]);

    useEffect(() => {
        if (!jigsawPlayId) {
            setJigsawTemplate(null);
            setJigsawLoading(false);
            return undefined;
        }
        let cancelled = false;
        setJigsawLoading(true);
        (async () => {
            try {
                const group = await ApiService.getData(
                    `/home/jigsaw/${encodeURIComponent(jigsawPlayId)}`
                );
                if (cancelled) return;
                const templates = Array.isArray(group?.templates) ? group.templates : [];
                setJigsawTemplate(pickDefaultJigsawTemplate(templates));
            } catch (e) {
                Logger.error("StorybookLinkedPuzzles jigsaw:", e);
                if (!cancelled) setJigsawTemplate(null);
            } finally {
                if (!cancelled) setJigsawLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [jigsawPlayId]);

    const hasWordLink = Boolean(wordPuzzleMeta);
    const hasSliding = Boolean(slidingPlayId);
    const hasJigsaw = Boolean(jigsawPlayId);
    if (!hasWordLink && !hasSliding && !hasJigsaw) return null;

    const playLabel = t("puzzle.card.play");
    const wordPlayTo = typeof wordPlayPath === "string" ? wordPlayPath : null;
    const wordPlayLoading = hasWordLink && wordPlayPath === undefined;
    const slidingPlayTo = hasSliding
        ? `/puzzle/play-sliding/${encodeURIComponent(slidingPlayId)}`
        : null;
    const jigsawPlayTo = hasJigsaw
        ? `/puzzle/play-jigsaw/${encodeURIComponent(jigsawPlayId)}`
        : null;

    return (
        <Box
            sx={{
                mt: 3,
                pt: 2.5,
                borderTop: "1px solid",
                borderColor: "divider",
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: TEXT.primary }}>
                {t("storybook.linked-puzzles-heading")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                {t("storybook.linked-puzzles-intro")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, alignItems: "stretch" }}>
            {hasWordLink ? (
                <LinkedTextPuzzleCard
                    meta={wordPuzzleMeta}
                    playTo={wordPlayTo}
                    playLabel={playLabel}
                    playLoading={wordPlayLoading}
                    gridPreview={wordGridPreview}
                    gridLoading={wordGridLoading}
                    t={t}
                    onPlayClick={() => trackStorybookPlay(wordPuzzleMeta?.type, wordPlayTo)}
                />
            ) : null}
            {hasSliding ? (
                <LinkedImagePuzzleCard
                    sectionTitle={t("landing.puzzle-type-sliding")}
                    hint={t("storybook.linked-sliding-hint")}
                    slidingPreview={slidingBoardPreview}
                    imageUrl={slidingFallbackImageUrl || storyImageUrl}
                    playTo={slidingPlayTo}
                    playLabel={playLabel}
                    onPlayClick={() => trackStorybookPlay("SLIDING", slidingPlayTo)}
                    disabled={slidingDisabled}
                    loading={
                        slidingLoading &&
                        !slidingBoardPreview &&
                        !(slidingFallbackImageUrl || storyImageUrl)
                    }
                />
            ) : null}
            {hasJigsaw ? (
                <LinkedImagePuzzleCard
                    sectionTitle={t("landing.puzzle-type-jigsaw")}
                    hint={t("storybook.linked-jigsaw-hint")}
                    imageUrl={storyImageUrl}
                    playTo={jigsawPlayTo}
                    playLabel={playLabel}
                    onPlayClick={() => trackStorybookPlay("JIGSAW", jigsawPlayTo)}
                    disabled={jigsawDisabled}
                    loading={jigsawLoading && !storyImageUrl}
                    overlay={<JigsawDieLinesOverlay template={jigsawTemplate} />}
                />
            ) : null}
            </Box>
        </Box>
    );
}
